import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Form,
  Layout,
  PageHeader,
  Spin,
  Space,
  Input,
  Divider,
  Select,
  Typography,
  Upload,
  Button,
  AutoComplete,
  Affix,
  message,
  Checkbox,
} from 'antd'
import { MinusCircleOutlined, UploadOutlined } from '@ant-design/icons'
import { userSelector } from '../../store/session'
import HeaderNavigation from '../../components/HeaderNavigation'
import { currentApplicationSelector, implementationOffersSelector, setApplicationItem } from '../../store/application'

import styles from './style.module.css'
import { createImplementationOffer, updateImplementationOffer } from '../../services/implementation-offer'
import { updateImplementation, updateImplementationItem } from '../../services/implementation'
import ProviderSelector from '../../components/ProviderSelector'
import { removeFromApplication } from '../../services/application-items'
import { createTransportation, deleteTransportation, updateTransportation } from '../../services/transportation'
import SISelect from '../../components/SISelect'
import SuggestionAutocomplete from '../../components/SuggestionAutocomplete'
import { addImplementationDetalisation, removeDetalisationImplementation } from '../../services/application'
import OfferModal from '../../components/OfferModal'
import DecimalInput from '../../components/DecimalInput'
import { APP_STATUS_DONE, APP_STATUS_PROGRESS } from '../../components/reports/constants'
import { calcLastWebSearchItem, searchInfoText } from '../../components/WebSearch/web-search-utils'
import { formatDate } from '../../utils/date-format'
import { formatDecimalAmount } from '../../utils/number-utils'
import VatSelect from '../../components/VatSelect'
import ProtectedAccess from '../../components/RoleBasedAccess/ProtectedAccess'

const { Option } = Select
const { Content } = Layout
const { Text } = Typography

const API_URL = process.env.REACT_APP_API_URL

const renderEstimateItem = (item) =>
  item?.estimate_field
    ? `${item?.estimate_field?.name} / ${item?.estimate_field?.category} / Цена: ${
        item?.estimate_field?.price || '-'
      } ${item?.estimate_field?.unit ? item?.estimate_field?.unit : ''}`
    : `${item?.category ? item?.category : '-'}`

const renderApplicationItem = (item) =>
  item
    ? `${item.name}, ${formatDecimalAmount(item.amount)} ${item.unit} ${
        item.description ? `(примечание: ${item.description})` : ''
      }`
    : ''

const paymentOptions = [
  {
    value: 'cashless',
    label: 'Безналичный',
  },
  {
    value: 'cash',
    label: 'Наличный',
  },
  {
    value: 'postponement',
    label: 'Безналичный пост оплата',
  },
  {
    value: 'postcashless',
    label: 'Безналичный пред оплата',
  },
]

const Implementation = () => {
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const [form] = Form.useForm()
  let navigate = useNavigate()
  const { user } = useSelector(userSelector)
  const [isLoading, setIsLoading] = useState(false)
  const currentApplication = useSelector(currentApplicationSelector)
  const implementationOffers = useSelector(implementationOffersSelector)
  const [paymentMethod, setPaymentMethod] = useState(currentApplication?.implementation?.payment_type)
  const implementationId = useMemo(() => searchParams.get('id'), [searchParams])
  const [fileToUpdate, setFileToUpdate] = useState(null)

  const implementationFormItems = Form.useWatch('implementation', form)

  const isProject = useMemo(() => user?.role?.type === 'project_manager', [user])

  const initFields = useMemo(
    () =>
      currentApplication?.implementation
        ? {
            implementation: currentApplication?.implementation?.implementation_items?.map((item) => ({
              id: item.id,
              name: item.name,
              amount: item.amount,
              price: item.price,
              unit: item.unit,
              provider: item.provider,
              warehouse: item.warehouse,
              description: item.description,
              article: item.article,
              vatType: item.vatType,
              vatValue: item.vatValue,
              isDetalisation: item.isDetalisation,
              isToStock: item.is_to_stock,
              correctedAmount: item.correctedAmount,
              correctedPrice: item.correctedPrice,
              estimate: currentApplication.application_items.find(
                (applicationItem) => applicationItem.id === item?.application_item?.id
              ),
              applicationItem: item?.application_item?.id,
            })),
            transportation: currentApplication?.transportation?.map((item) => ({
              id: item.id,
              from: item.from,
              to: item.to,
              price: item.price,
              vatType: item.vatType,
              vatValue: item.vatValue,
              paymentType: item.paymentType,
              provider: item.provider,
            })),
          }
        : {},
    [currentApplication]
  )

  const fileList = useMemo(
    () =>
      implementationOffers
        ? implementationOffers
            .filter((file) => file.type === 'final')
            .map((file) => ({
              uid: file.id,
              name: file?.offer?.name,
              status: APP_STATUS_DONE,
              url: `${API_URL}${file?.offer?.url}`,
              amount: file?.amount,
              number: file?.number,
              date: file?.date,
            }))
        : [],
    [implementationOffers]
  )

  const offerFileList = useMemo(
    () =>
      implementationOffers
        ? implementationOffers
            .filter((file) => file.type === 'offer')
            .map((file) => ({
              uid: file.id,
              name: file?.offer?.name,
              status: APP_STATUS_DONE,
              url: `${API_URL}${file?.offer?.url}`,
              amount: file.offer?.amount,
              number: file.offer?.number,
              date: file.offer?.date,
            }))
        : [],
    [implementationOffers]
  )

  const onFileUpload = ({ onSuccess, onProgress, onError, file }, type) => {
    const formData = new FormData()
    const data = {
      user: user.id,
      type,
      implementation: implementationId,
    }
    formData.append('data', JSON.stringify(data))
    formData.append('files.offer', file, file.name)
    createImplementationOffer({
      data: formData,
    })
      .then((response) => {
        onSuccess(file, response)
      })
      .catch((error) => {
        message.error('Ошибка при загрузке файла')
        onError(error)
      })
  }

  const onFileRemove = (file) => {
    const fileId = file?.xhr ? file?.xhr?.data?.id : file.uid
    if (fileId) {
      return updateImplementationOffer(fileId, {
        data: {
          type: 'remove',
          user: user.id,
        },
      })
    } else {
      message.error('Ошибка при удалении файла')
      return Promise.reject('Empty file uid')
    }
  }

  const onFormUpdate = (data) => {
    const promises = []
    setIsLoading(true)
    data?.implementation?.forEach((item) => {
      const request = updateImplementationItem(item.id, {
        data: {
          name: item.name,
          price: item.price,
          provider: item.provider,
          unit: item.unit,
          amount: item.amount,
          article: item.article,
          description: item.description,
          warehouse: item.warehouse,
          vatType: item.vatType,
          vatValue: item.vatValue,
          correctedAmount: item.correctedAmount,
          correctedPrice: item.correctedPrice,
          is_to_stock: item.isToStock,
        },
      })
      promises.push(request)
    })
    data?.transportation?.forEach((item) => {
      const data = {
        from: item.from,
        to: item.to,
        price: item.price,
        vatType: item.vatType,
        vatValue: item.vatValue,
        paymentType: item.paymentType,
        provider: item.provider,
        application: currentApplication?.id,
      }
      if (!item?.id) {
        const request = createTransportation({
          data,
        }).catch(() => {
          message.error('Ошибка при сохранении информации о доставке')
        })
        promises.push(request)
      } else {
        const request = updateTransportation(item?.id, {
          data,
        }).catch(() => {
          message.error('Ошибка при сохранении информации о доставке')
        })
        promises.push(request)
      }
    })

    currentApplication?.transportation?.forEach((item) => {
      const result = data?.transportation?.find((formItem) => formItem?.id === item?.id)
      if (!result && item?.id) {
        const request = deleteTransportation(item?.id).catch(() => {
          message.error('Ошибка при сохранении информации о доставке')
        })
        promises.push(request)
      }
    })

    Promise.allSettled(promises)
      .then(() => {
        message.success('Заявка успешно сохранена')
      })
      .catch(() => message.error('Ошибка при сохранении заявки'))
      .finally(() => setIsLoading(false))
  }

  const onCloseHandler = () => {
    navigate(-1)
  }

  const updatePaymentMethod = (value) => {
    const id = currentApplication?.implementation?.id
    const currentValue = currentApplication?.implementation?.payment_type
    if (id && value !== currentValue) {
      setIsLoading(true)
      updateImplementation(id, {
        data: {
          payment_type: value,
        },
      })
        .then(() => {
          setPaymentMethod(value)
          message.success('Способ оплаты успешно изменен')
        })
        .catch(() => {
          message.error('Ошибка способ оплаты не сохранен')
        })
        .finally(() => setIsLoading(false))
    }
  }

  const removeItem = (field, remove) => {
    const data = {
      data: {
        implementationItem: field?.id,
        application: currentApplication?.id,
      },
    }
    setIsLoading(true)
    removeFromApplication(field?.applicationItem, data)
      .then(() => {
        message.success('Позиция успешно удалена')
        remove()
      })
      .catch((err) => {
        const errText = err?.response?.data?.error?.message
        if (errText) {
          message.error(`Ошибка удаления позиции: ${errText}`)
        } else {
          message.error('Ошибка удаления позиции')
        }
      })
      .finally(() => setIsLoading(false))
  }

  const addDetalisation = (field, add) => {
    const data = {
      implementation: currentApplication?.implementation?.id,
      application_item: field.applicationItem,
    }
    setIsLoading(true)
    addImplementationDetalisation(data)
      .then((response) => {
        add({
          ...response?.data,
          applicationItem: field.applicationItem,
          estimate: field.estimate,
        })
        message.success('Позиция успешно добавлена')
      })
      .catch(() => {
        message.error('Ошибка добавления позиции')
      })
      .finally(() => setIsLoading(false))
  }

  const removeDetalisation = (implItem, remove) => {
    const data = {
      data: {
        implementation: implItem?.id,
        application: currentApplication?.id,
      },
    }
    setIsLoading(true)
    removeDetalisationImplementation(implItem?.id, data)
      .then(() => {
        message.success('Детализация успешно удалена')
        remove()
      })
      .catch((err) => {
        const errText = err?.response?.data?.error?.message
        if (errText) {
          message.error(`Ошибка удаления детализации: ${errText}`)
        } else {
          message.error('Ошибка удаления детализации')
        }
      })
      .finally(() => setIsLoading(false))
  }

  const onEstimateEditHandler = (record) => {
    dispatch(setApplicationItem({ applicationItem: record, implementationId }))
    navigate(`/application/new?update=true${!record ? `&project=${currentApplication?.project?.id}` : ''}`, {
      replace: true,
    })
  }

  const removeAppItemEnabled = useCallback(
    (appItemId, isDetalisation) => {
      return (
        !isLoading &&
        !isDetalisation &&
        (currentApplication?.current_state === APP_STATUS_PROGRESS || isProject) &&
        !implementationFormItems
          ?.filter((implItem) => appItemId === implItem.applicationItem)
          .some((implItem) => implItem.isDetalisation && implItem.isDetalisation)
      )
    },
    [isLoading, currentApplication?.current_state, isProject, implementationFormItems]
  )

  const webSearchEnabled = useCallback(
    (appItem) => appItem && appItem.estimate_field,
    [isLoading, currentApplication, implementationId, implementationFormItems]
  )

  const addDetalisationEnabled = useCallback(
    (isDetalisation) =>
      !isLoading && !isDetalisation && (currentApplication?.current_state === APP_STATUS_PROGRESS || isProject),
    [isLoading, currentApplication?.current_state, isProject]
  )

  const removeDetalisationEnabled = useCallback(
    (isDetalisation) =>
      !isLoading && isDetalisation && (currentApplication?.current_state === APP_STATUS_PROGRESS || isProject),
    [isLoading, currentApplication?.current_state, isProject]
  )

  const EditLink = ({ appItem }) => (
    <Button type="link" onClick={() => onEstimateEditHandler(appItem)}>
      Редактировать
    </Button>
  )

  const RemoveLink = ({ onRemove = {} }) => (
    <div>
      <Button type="link" onClick={onRemove}>
        <Text type="danger">Удалить из заявки</Text>
      </Button>
    </div>
  )

  const WebSearchLink = ({ appItem }) => {
    const lastItem = calcLastWebSearchItem(appItem.web_search_items)
    const info = searchInfoText(lastItem, appItem.estimate_field)
    return info ? (
      <Button type="link" onClick={() => onEstimateEditHandler(appItem)}>
        <Text type={info.state}>{info.text}</Text>
      </Button>
    ) : null
  }

  return (
    <Layout>
      <HeaderNavigation activeKey="1" />
      <Content className={styles.content}>
        <Affix offsetTop={75}>
          <PageHeader
            ghost={false}
            title={`Заявка ${currentApplication?.id} в работе`}
            extra={[
              <Button key="close" onClick={onCloseHandler}>
                Закрыть
              </Button>,
              <Button
                disabled={isProject ? false : currentApplication?.current_state !== APP_STATUS_PROGRESS}
                key="save"
                type="primary"
                onClick={() => form.submit()}
              >
                Сохранить изменения
              </Button>,
            ]}
          />
        </Affix>
        {currentApplication && (
          <div>
            <div
              style={{
                marginLeft: 9,
                paddingTop: 10,
              }}
            >
              <div>
                <Text strong>Проект: </Text>
                <Text>{`${currentApplication?.project?.name}`}</Text>
              </div>
              <div>
                <Text strong>Способ оплаты: </Text>
                <Select
                  loading={isLoading}
                  disabled={isLoading || currentApplication?.current_state !== APP_STATUS_PROGRESS}
                  size="small"
                  defaultValue="cashless"
                  value={paymentMethod}
                  onChange={updatePaymentMethod}
                  style={{ minWidth: 250 }}
                  options={paymentOptions}
                  dropdownMatchSelectWidth={false}
                />
              </div>
            </div>
            <Divider plain />
            <Spin spinning={isLoading}>
              <Form
                initialValues={initFields}
                name="application"
                layout="vertical"
                className={styles.form}
                form={form}
                onFinish={onFormUpdate}
              >
                <Form.List name="implementation">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key}>
                          <Space className={styles.info} size={0}>
                            <Space style={{ display: 'flex', width: '100%', paddingTop: 10 }} align="baseline">
                              <div>
                                <Text strong>Из сметы: </Text>
                                <Text>{renderEstimateItem(form.getFieldValue('implementation')[name]?.estimate)}</Text>
                              </div>
                              <Space>
                                <div>
                                  <EditLink appItem={form.getFieldValue('implementation')[name]?.estimate} />
                                </div>
                                {removeAppItemEnabled(
                                  form.getFieldValue('implementation')[name]?.applicationItem,
                                  form.getFieldValue('implementation')[name]?.isDetalisation
                                ) && (
                                  <div>
                                    <RemoveLink
                                      onRemove={() =>
                                        removeItem(form.getFieldValue('implementation')[name], () => remove(name))
                                      }
                                    />
                                  </div>
                                )}
                                {webSearchEnabled(form.getFieldValue('implementation')[name]?.estimate) && (
                                  <div>
                                    <ProtectedAccess allowedRoles={['project_manager', 'supplier']}>
                                      <WebSearchLink appItem={form.getFieldValue('implementation')[name]?.estimate} />
                                    </ProtectedAccess>
                                  </div>
                                )}
                              </Space>
                            </Space>
                            <div>
                              <Text strong>Из заявки: </Text>
                              <Text>{renderApplicationItem(form.getFieldValue('implementation')[name]?.estimate)}</Text>
                            </div>
                            <div>
                              {form.getFieldValue('implementation')[name]?.isDetalisation && (
                                <Text type="danger">Детализация</Text>
                              )}
                            </div>
                          </Space>
                          <Space
                            direction="vertical"
                            size={0}
                            style={{ display: 'flex', width: '100%', paddingTop: 10 }}
                          >
                            <Space style={{ display: 'flex', width: '100%' }} align="baseline" wrap>
                              <Form.Item style={{ display: 'none' }} {...restField} name={[name, 'id']} hidden>
                                <Input />
                              </Form.Item>
                              <Form.Item {...restField} label="Артикул" name={[name, 'article']}>
                                <Input />
                              </Form.Item>
                              <Form.Item
                                style={{ minWidth: 250 }}
                                {...restField}
                                label="Наименование из КП"
                                name={[name, 'name']}
                              >
                                <SuggestionAutocomplete />
                              </Form.Item>
                              <Form.Item {...restField} label="Количество" name={[name, 'amount']}>
                                <DecimalInput style={{ maxWidth: 100, minWidth: 100 }} />
                              </Form.Item>
                              <Form.Item {...restField} label="Корректировка кол-ва" name={[name, 'correctedAmount']}>
                                <DecimalInput style={{ maxWidth: 100, minWidth: 100 }} />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                label="Единица измерения"
                                name={[name, 'unit']}
                                style={{ maxWidth: 200, minWidth: 200 }}
                              >
                                <SISelect />
                              </Form.Item>
                              <Form.Item {...restField} label="Цена за ед." name={[name, 'price']}>
                                <DecimalInput style={{ maxWidth: 120, minWidth: 100 }} />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                label="Корректировка цены за ед."
                                name={[name, 'correctedPrice']}
                              >
                                <DecimalInput style={{ maxWidth: 120, minWidth: 100 }} />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                label="НДС"
                                name={[name, 'vatType']}
                                style={{ maxWidth: 150, minWidth: 150 }}
                              >
                                <Select dropdownMatchSelectWidth={false}>
                                  <Option value="none">Без НДС</Option>
                                  <Option value="up">НДС сверху</Option>
                                  <Option value="include">НДС в сумме</Option>
                                </Select>
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                label="% НДС"
                                name={[name, 'vatValue']}
                                style={{ maxWidth: 100, minWidth: 100 }}
                              >
                                <VatSelect />
                              </Form.Item>
                              <Form.Item {...restField} label="Поставщик" name={[name, 'provider']}>
                                <ProviderSelector />
                              </Form.Item>
                            </Space>
                            <Space style={{ display: 'flex', width: '100%' }} align="baseline" wrap>
                              <Form.Item
                                {...restField}
                                label="Примечание"
                                name={[name, 'description']}
                                style={{ width: 1000 }}
                              >
                                <Input style={{ width: '100%' }} />
                              </Form.Item>
                            </Space>
                            <Space style={{ display: 'flex', width: '100%' }} align="baseline" wrap>
                              <Form.Item
                                {...restField}
                                label="Склад"
                                name={[name, 'warehouse']}
                                style={{ minWidth: 250 }}
                              >
                                <AutoComplete
                                  options={[{ value: 'Муром, Радиозаводское шоссе, 2Б' }]}
                                  dropdownMatchSelectWidth={false}
                                />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                style={{ textAlign: 'center', marginLeft: 15, minWidth: 80 }}
                                label="Добавить на склад объекта"
                                name={[name, 'isToStock']}
                                valuePropName="checked"
                                initialValue={false}
                              >
                                <Checkbox />
                              </Form.Item>
                            </Space>
                            <Space style={{ display: 'flex', width: '100%' }} align="baseline" wrap>
                              <Form.Item label="Действия">
                                {addDetalisationEnabled(form.getFieldValue('implementation')[name]?.isDetalisation) && (
                                  <Button
                                    onClick={() => addDetalisation(form.getFieldValue('implementation')[name], add)}
                                  >
                                    Добавить детализацию
                                  </Button>
                                )}
                                {removeDetalisationEnabled(
                                  form.getFieldValue('implementation')[name]?.isDetalisation
                                ) && (
                                  <Button
                                    onClick={() =>
                                      removeDetalisation(form.getFieldValue('implementation')[name], () => remove(name))
                                    }
                                  >
                                    <Text type="danger">Удалить детализацию</Text>
                                  </Button>
                                )}
                              </Form.Item>
                            </Space>
                          </Space>
                          <Divider plain />
                        </div>
                      ))}
                    </>
                  )}
                </Form.List>
                <Form.List name="transportation">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key}>
                          <Space style={{ display: 'flex', width: '100%' }} align="baseline" wrap>
                            <Form.Item {...restField} label="Идентификатор" name={[name, 'id']} hidden>
                              <Input />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="Адрес отправления"
                              name={[name, 'from']}
                              style={{ minWidth: 300 }}
                            >
                              <Input />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="Адрес доставки"
                              name={[name, 'to']}
                              style={{ minWidth: 300 }}
                            >
                              <Input />
                            </Form.Item>
                            <Form.Item {...restField} label="Стоимость доставки" name={[name, 'price']}>
                              <DecimalInput style={{ maxWidth: 150, minWidth: 150 }} />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="НДС"
                              name={[name, 'vatType']}
                              style={{ maxWidth: 150, minWidth: 150 }}
                            >
                              <Select dropdownMatchSelectWidth={false}>
                                <Option value="none">Без НДС</Option>
                                <Option value="up">НДС сверху</Option>
                                <Option value="include">НДС в сумме</Option>
                              </Select>
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="% НДС"
                              name={[name, 'vatValue']}
                              style={{ maxWidth: 100, minWidth: 100 }}
                            >
                              <VatSelect />
                            </Form.Item>
                            <Form.Item {...restField} label="Способ оплаты" name={[name, 'paymentType']}>
                              <Select defaultValue="cashless" style={{ minWidth: 200 }} options={paymentOptions} />
                            </Form.Item>
                            <Form.Item {...restField} label="Поставщик" name={[name, 'provider']}>
                              <ProviderSelector />
                            </Form.Item>
                            <Form.Item label=" ">
                              <MinusCircleOutlined
                                disabled={isLoading || currentApplication?.current_state !== APP_STATUS_PROGRESS}
                                onClick={() => remove(name)}
                              />
                            </Form.Item>
                          </Space>
                        </div>
                      ))}
                      <Form.Item>
                        <Button
                          disabled={isLoading || currentApplication?.current_state !== APP_STATUS_PROGRESS}
                          type="dashed"
                          onClick={() => add()}
                          block
                        >
                          Добавить доставку
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
                <Space style={{ marginLeft: 9 }} size={30} align="start">
                  <Form.Item style={{ width: 500 }} label="Документы" name="files">
                    <Upload
                      fileList={fileList}
                      onRemove={onFileRemove}
                      customRequest={(e) => onFileUpload(e, 'final')}
                      showUploadList={{
                        showRemoveIcon: currentApplication?.current_state === APP_STATUS_PROGRESS,
                      }}
                      itemRender={(originNode, file) => (
                        <div>
                          {originNode}
                          <div>
                            <Text>
                              № {file.number || '-'} от {formatDate(file?.date)}
                            </Text>
                            <Button type="link" onClick={() => setFileToUpdate(file)}>
                              Редактировать
                            </Button>
                          </div>
                        </div>
                      )}
                    >
                      <Button
                        disabled={isLoading || currentApplication?.current_state !== APP_STATUS_PROGRESS}
                        icon={<UploadOutlined />}
                      >
                        Загрузить коммерческие предложения
                      </Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item style={{ width: 500 }} label="Альтернативные документы" name="files">
                    <Upload
                      fileList={offerFileList}
                      customRequest={(e) => onFileUpload(e, 'offer')}
                      showUploadList={{
                        showRemoveIcon: currentApplication?.current_state === APP_STATUS_PROGRESS,
                      }}
                      onRemove={onFileRemove}
                    >
                      <Button
                        disabled={isLoading || currentApplication?.current_state !== APP_STATUS_PROGRESS}
                        icon={<UploadOutlined />}
                      >
                        Загрузить коммерческие предложения
                      </Button>
                    </Upload>
                  </Form.Item>
                </Space>
              </Form>
            </Spin>
          </div>
        )}
        <OfferModal
          showModal={fileToUpdate}
          id={fileToUpdate?.uid}
          number={fileToUpdate?.number}
          date={fileToUpdate?.date}
          onClose={() => setFileToUpdate(null)}
        />
      </Content>
    </Layout>
  )
}

export default Implementation
